/**
 * Privacy rounding — currency-neutral downward quantization.
 *
 * The published cap is the lowest submitted comfortable maximum, rounded DOWN to a
 * human-friendly quantum of roughly 5% of that minimum. Deliberately stricter than a
 * majority poll and different from the "overlap range" used by group-travel products.
 *
 * Two invariants hold for every input, asserted below:
 *   1. the result never exceeds the raw ceiling;
 *   2. the result is never rounded upward.
 *
 * ---
 *
 * Why this replaced a fixed table of absolute bands.
 *
 * The original rule ("under 100 → nearest 5, 100–249 → nearest 10, …") is defined in
 * absolute major units, so the amount of blur it provides depends entirely on how much
 * one unit of the currency is worth. At a comparable real budget of about €270:
 *
 *     EUR 270  → step 25   →  9.3% blur
 *     JPY 45,007 → step 250 →  0.6% blur
 *     KRW 400,007 → step 250 → 0.06% blur
 *
 * In won the "rounded" cap was the submitted minimum to within a rounding error, which
 * would have made the privacy claim on /privacy and /how-it-works untrue for six of the
 * thirty supported currencies. A relative quantum fixes that without any exchange-rate
 * data: 5% of the amount is 5% of the amount in every currency.
 *
 * ---
 *
 * Why a ladder rather than "pick the nearest candidate to 5%".
 *
 * Choosing the nearest quantum to 5% independently for each input is not monotonic. With
 * that approach a raw minimum of 447.00 selects a €20 quantum and publishes €440, while
 * 448.00 tips over to a €25 quantum and publishes €425 — a *higher* private answer
 * producing a *lower* group cap. That is both confusing and mildly exploitable.
 *
 * The ladder below fixes the switch points at multiples of the quantum they switch into,
 * which makes the whole function monotonic: crossing a threshold always lands exactly on
 * a multiple of the new, larger quantum, so the cap steps up rather than down.
 *
 * The thresholds are the geometric midpoints between the ideal amounts for adjacent
 * quanta (a quantum q is "ideal" at raw = 20q, i.e. 5%), snapped to a multiple of the
 * larger quantum. That yields the repeating pattern 15 / 30 / 45 / 75 per decade and an
 * achieved quantum of 3.33%–7.14% of the raw minimum.
 *
 * Two documented exceptions to the 3–8% band, both in the "where practical" sense:
 *
 *   * 30–74 minor units. The 2.5 rung is not a whole minor unit at this scale, so the
 *     rung is merged into the 2 quantum. The fraction dips as low as 2.70% at 74 minor
 *     units — that is 0.74 of a major unit in a two-decimal currency.
 *   * Below 15 minor units. No quantum in the band exists, so the exact minimum is
 *     published. This is also what keeps the cap from ever being zero.
 */

import { minorUnitFactor } from './currencies.js';

/** Target quantum as a fraction of the raw minimum. */
export const TARGET_QUANTUM_FRACTION = 0.05;
/** The quantum is kept inside this fraction band wherever it is possible to do so. */
export const MIN_QUANTUM_FRACTION = 0.03;
export const MAX_QUANTUM_FRACTION = 0.08;

/**
 * The repeating ladder, in units of the current decade scale.
 *
 * `from` is inclusive and expressed as a multiple of the scale; the mantissas are the
 * 1 / 2 / 2.5 / 5 series. The pattern repeats every power of ten: 150 × scale is simply
 * 15 × (10 × scale).
 */
const LADDER = [
  { from: 15, mantissa: 1 },
  { from: 30, mantissa: 2 },
  { from: 45, mantissa: 2.5 },
  { from: 75, mantissa: 5 },
] as const;

/** Below this many minor units no quantum can stay inside the target band. */
const SMALLEST_LADDER_START = 15;

/**
 * The quantum for a raw minimum, in integer minor units.
 *
 * Monotonically non-decreasing in `rawMinor`, which is what makes the published cap
 * monotonic too.
 */
export function quantumForMinor(rawMinor: number): number {
  // Amounts too small for any quantum in the 3–8% band: quantise by one minor unit,
  // which publishes the exact minimum. There is nothing meaningful to blur at this
  // scale, and it is the only way to avoid a cap of zero.
  if (rawMinor < SMALLEST_LADDER_START) return 1;

  // The decade this amount sits in: the largest power of ten with rawMinor >= 15 * scale.
  let scale = 1;
  while (rawMinor >= SMALLEST_LADDER_START * 10 * scale) scale *= 10;

  let mantissa = LADDER[LADDER.length - 1]!.mantissa;
  for (const rung of LADDER) {
    if (rawMinor >= rung.from * scale) mantissa = rung.mantissa;
  }

  // 2.5 is only an integer number of minor units from the second decade up. At scale 1
  // the 45–75 rung keeps the previous quantum of 2, which costs a little blur on amounts
  // below 0.75 major units and keeps every quantum a whole minor unit.
  if (mantissa === 2.5 && scale === 1) return 2;

  const quantum = mantissa * scale;
  return Number.isInteger(quantum) ? quantum : Math.round(quantum);
}

export interface RoundResult {
  /** The published group-safe cap, in integer minor units. */
  readonly capMinor: number;
  /** The quantum applied, in integer minor units. */
  readonly stepMinor: number;
  /** The quantum as a fraction of the raw minimum, for tests and documentation. */
  readonly quantumFraction: number;
  /**
   * True when the amount was too small for any quantum inside the 3–8% band, so the
   * exact minimum is published. Only reachable below 15 minor units.
   */
  readonly belowQuantisationFloor: boolean;
}

/**
 * Round a raw minimum DOWN to the group-safe cap.
 *
 * @param rawMinor  minimum of all submitted amounts, in integer minor units, > 0
 * @param currency  ISO 4217 code from the allowlist
 */
export function roundDownToSafeCap(rawMinor: number, currency: string): RoundResult {
  if (!Number.isSafeInteger(rawMinor) || rawMinor <= 0) {
    throw new Error('roundDownToSafeCap requires a positive integer minor amount');
  }
  // Validates the currency even though the rule no longer depends on its scale, so an
  // unsupported code can never reach a published result.
  minorUnitFactor(currency);

  const stepMinor = quantumForMinor(rawMinor);
  const capMinor = Math.floor(rawMinor / stepMinor) * stepMinor;

  // Hard invariants. These are cheap and this is the one calculation that must not drift.
  if (capMinor > rawMinor) {
    throw new Error('invariant violated: safe cap exceeds the raw ceiling');
  }
  if (!Number.isSafeInteger(capMinor) || capMinor <= 0) {
    throw new Error('invariant violated: safe cap is not a positive integer');
  }

  return {
    capMinor,
    stepMinor,
    quantumFraction: stepMinor / rawMinor,
    belowQuantisationFloor: rawMinor < SMALLEST_LADDER_START,
  };
}

/**
 * Worked examples for the explanatory pages, computed from the real function so the
 * documentation cannot drift away from the behaviour.
 *
 * @param currency  the currency to express the examples in
 * @param majorAmounts  raw minimums, in major units
 */
export function roundingExamples(
  currency: string,
  majorAmounts: readonly number[],
): Array<{ rawMinor: number; capMinor: number; stepMinor: number }> {
  const factor = minorUnitFactor(currency);
  return majorAmounts.map((major) => {
    const rawMinor = Math.round(major * factor);
    const { capMinor, stepMinor } = roundDownToSafeCap(rawMinor, currency);
    return { rawMinor, capMinor, stepMinor };
  });
}
